/**
 * The capture harness's in-page API.
 *
 * Exposed on `window.__BA_HARNESS__` so `tools/capture.mjs` can drive the game
 * to a named deterministic state without knowing anything about its internals.
 */

import { boot, type Game } from '../boot.js'
import { SHOTS, shotByName, shotNames, type Shot } from './shots.js'
import { LocalRelay } from '../net/local.js'
import { populatorFor } from './populate.js'
import { defById } from '../sim/data/entities.js'
import { Build } from '../sim/build.js'

export interface Harness {
  shotNames(): string[]
  /** The motion-pair offset for a shot, or null if it is a still. */
  motionOffset(name: string): [number, number] | null
  /** Boot a fresh game at the named shot's state. Disposes any previous one. */
  setup(name: string, interactive?: boolean): { ok: boolean; error?: string; hud?: boolean }
  /** Advance one frame. Used by the profiler to time rendering in the page. */
  frame(dtMs: number): void
  /** Generated palette, plus the subset entities are drawn from. */
  palette(): { colors: number[]; owner: number[]; ownerBySlot: number[][] }
  /** Renderer counters, for the performance gate. */
  stats(): { drawCalls: number; triangles: number; programs: number }
  /** One binary silhouette per registry entity, for the critic (U19, U22). */
  silhouettes(): Array<{ key: string; width: number; height: number; mask: number[] }>
  /** Nudge the camera by internal pixels and re-render. For the motion pair. */
  nudge(dx: number, dy: number): void
  ready(): boolean
  dispose(): void
}

declare global {
  interface Window {
    __BA_HARNESS__?: Harness
    __BA_SHOT_READY__?: boolean
  }
}

export function installHarness(mount: HTMLElement): Harness {
  let game: Game | null = null

  const harness: Harness = {
    shotNames,

    motionOffset(name: string) {
      return shotByName(name)?.motionOffset ?? null
    },

    setup(name: string, interactive = false) {
      window.__BA_SHOT_READY__ = false
      const shot = shotByName(name)
      if (!shot) return { ok: false, error: `unknown shot '${name}'. Known: ${shotNames().join(', ')}` }
      try {
        harness.dispose()
        game = bootShot(mount, shot, interactive)
        window.__BA__ = game
        window.__BA_SHOT_READY__ = true
        return { ok: true, hud: shot.hud === true }
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) }
      }
    },

    frame(dtMs: number) {
      game?.frame(dtMs)
    },

    stats() {
      if (!game) return { drawCalls: 0, triangles: 0, programs: 0 }
      return {
        drawCalls: game.renderer.entityDrawCalls,
        triangles: game.renderer.entityTriangles,
        programs: game.renderer.programCount,
      }
    },

    palette() {
      if (!game) return { colors: [], owner: [], ownerBySlot: [] }
      const p = game.renderer.palette
      // `owner` is every owner ramp concatenated, which loses the slot boundaries.
      // `ownerBySlot` keeps them, so a metric can ask whether *each* army separates
      // from the ground rather than whether they do on average — one bright faction
      // otherwise carries a camouflaged one past the bar.
      const owner: number[] = []
      const ownerBySlot: number[][] = []
      for (const ramp of p.ownerRamps) {
        const slot: number[] = []
        for (const i of ramp.indices) slot.push(...p.rgb(i))
        ownerBySlot.push(slot)
        owner.push(...slot)
      }
      return { colors: Array.from(p.colors), owner, ownerBySlot }
    },

    silhouettes() {
      if (!game) return []
      return game.renderer.captureSilhouettes()
    },

    nudge(dx: number, dy: number) {
      if (!game) return
      game.renderer.panBy(dx, dy)
      game.frame(0)
    },

    ready() {
      return game !== null
    },

    dispose() {
      game?.stop()
      if (window.__BA__ === game) delete window.__BA__
      game = null
      for (const child of Array.from(mount.children)) child.remove()
    },
  }

  window.__BA_HARNESS__ = harness
  return harness
}

function bootShot(mount: HTMLElement, shot: Shot, interactive: boolean): Game {
  const game = boot({
    mount,
    match: shot.match,
    localSlot: 0,
    manualFrames: true,
    noInput: !interactive,
    noHud: shot.hud !== true,
    noAudio: true,
    quantize: shot.quantize ?? false,
  })

  // Population runs before the seek, so the simulation settles the state for
  // the requested number of ticks rather than freezing it mid-spawn.
  populatorFor(shot.populate)?.(game.sim)
  // A populator may have edited the map — the model sheets flatten it — and the
  // terrain mesh was built from the map as generated.
  game.renderer.refreshTerrain()

  if (shot.commands?.length) {
    const relay = game.sim.relay as LocalRelay
    const pending = shot.commands.slice().sort((a, b) => a.tick - b.tick)
    let next = 0
    for (let tick = 0; tick < shot.tick; tick++) {
      while (next < pending.length && pending[next].tick === tick) {
        const entry = pending[next++]
        relay.submit({ ...entry.command, slot: entry.slot ?? 0 } as never)
      }
      if (!game.sim.step()) break
    }
  } else {
    game.seekTo(shot.tick)
  }

  if (shot.ghost) {
    const def = defById(shot.ghost.kind)
    if (def) {
      const build = game.sim.registry.get<Build>('build')
      game.renderer.setPlacementGhost({
        cx: shot.ghost.cx,
        cy: shot.ghost.cy,
        w: def.width,
        h: def.height,
        legal: build.footprintFailure(0, def, shot.ghost.cx, shot.ghost.cy, true) === null,
      })
    }
  }

  if (shot.localSlot !== undefined) game.renderer.setLocalSlot(shot.localSlot)

  const [cx, cy] = shot.center ?? [64, 64]
  game.renderer.centerOn(cx, cy)
  // Two frames: the first flushes any lazily compiled shader so the captured
  // one is never a partially-warmed pipeline.
  game.frame(0)
  game.frame(0)
  return game
}

export { SHOTS }
